import axios from 'axios';

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
};

const api = axios.create({
  headers: {
    'X-CSRF-Token': csrfToken(),
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Look Templates
export const fetchLooks = (season) => {
  const params = season ? { season } : {};
  return api.get('/admin/favorites_designer/looks', { params }).then((r) => r.data);
};

export const createLook = (lookData) => api.post('/admin/favorites_designer/looks', { look_template: lookData }).then((r) => r.data);

export const updateLook = (id, lookData) => api.patch(`/admin/favorites_designer/looks/${id}`, { look_template: lookData }).then((r) => r.data);

export const deleteLook = (id) => api.delete(`/admin/favorites_designer/looks/${id}`).then((r) => r.data);

// Curation toggle — separate from publish. Deactivating drops the look from the
// season's exported CSV (EC2 stops rendering it) and from the customer store.
// The server re-exports the season's artifact as part of this call.
export const setLookActive = (id, active) =>
  api.post(`/admin/favorites_designer/looks/${id}/active`, { active }).then(r => r.data);

// Favorites Layout
export const fetchLayout = (packageId, season) => api.get('/admin/favorites_designer/layout', { params: { package_id: packageId, season } }).then((r) => r.data);

// Fetch reference poses separately (can be 50+ MB) — loaded in background after layout
export const fetchLayoutPoses = (packageId, season) => api.get('/admin/favorites_designer/layout', { params: { package_id: packageId, season, include_poses: '1' } }).then((r) => r.data?.reference_poses || []);

export const saveLayout = (data) => api.post('/admin/favorites_designer/layout', data).then((r) => r.data);

// Reference poses by season (for LookEditor when layout tab hasn't loaded)
export const fetchReferencePosesForSeason = (season) => api.get('/admin/favorites_designer/reference_poses_for_season', { params: { season } }).then((r) => r.data);

// Packages
export const fetchPackages = () => api.get('/admin/favorites_designer/packages_list').then((r) => r.data);

// Backgrounds
export const fetchBackgroundSets = () => api.get('/admin/favorites_designer/background_sets_list').then((r) => r.data);

export const fetchBackgrounds = (setId) => api.get('/admin/favorites_designer/backgrounds_for_set', { params: { set_id: setId } }).then((r) => r.data);

// Presigned URL for S3 objects
export const fetchPresignedUrl = (key) => api.get('/admin/favorites_designer/presigned_url', { params: { key } }).then((r) => r.data.url);

// Batch presigned URLs for S3 objects
export const fetchPresignedUrls = (keys) => api.post('/admin/favorites_designer/presigned_urls', { keys }).then((r) => r.data.urls);

// Look purchase counts
export const fetchLookPurchaseCounts = () => api.get('/admin/favorites_designer/look_purchase_counts').then((r) => r.data.counts);

// Database poses (existing processed images)
export const fetchDatabasePoses = (season, limit = 50) => api.get('/admin/favorites_designer/database_poses', { params: { season, limit } }).then((r) => r.data);

// Random sample of real students + their pose thumbnails for the Batch Test.
export const fetchBatchTestStudents = (season, limit = 8) => api.get('/admin/favorites_designer/batch_test_students', { params: { season, limit } }).then((r) => r.data);

// Buy All image upload
export const uploadBuyAll = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post('/admin/favorites_designer/upload_buy_all', formData, {
    headers: {
      'X-CSRF-Token': csrfToken(),
      'Content-Type': 'multipart/form-data',
    },
  }).then((r) => r.data);
};

// Popularity report
export const fetchPopularityReport = (season, packageId) => api.get('/admin/favorites_designer/popularity_report', { params: { season, package_id: packageId } }).then((r) => r.data);

// Canvas-background upload — same shape as uploadOverlay. Returns
// { s3_path, filename } which the caller writes onto canvas_filepath /
// canvas_filename so the look survives reload and prints correctly.
export const uploadCanvasBg = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post('/admin/favorites_designer/upload_canvas_bg', formData, {
    headers: {
      'X-CSRF-Token': csrfToken(),
      'Content-Type': 'multipart/form-data',
    },
  }).then((r) => r.data);
};

// Promo GIF Campaigns — grouping for gif_slide look templates. A season
// can have many (JD0, JD3, Black Friday, ...); each owns its own exported
// CSV and EC2 output prefix.
export const fetchGifCampaigns = (season) => {
  const params = season ? { season } : {};
  return api.get('/admin/favorites_designer/gif_campaigns', { params }).then((r) => r.data);
};

export const createGifCampaign = (payload) => api.post('/admin/favorites_designer/gif_campaigns', { promo_gif_campaign: payload }).then((r) => r.data);

export const updateGifCampaign = (id, payload) => api.patch(`/admin/favorites_designer/gif_campaigns/${id}`, { promo_gif_campaign: payload }).then((r) => r.data);

export const deleteGifCampaign = (id) => api.delete(`/admin/favorites_designer/gif_campaigns/${id}`).then((r) => r.data);

// Overlay upload
export const uploadOverlay = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post('/admin/favorites_designer/upload_overlay', formData, {
    headers: {
      'X-CSRF-Token': csrfToken(),
      'Content-Type': 'multipart/form-data',
    },
  }).then((r) => r.data);
};

// "Render true proof" (#4559): ask the REAL EC2 renderer to render this
// season's live spec against the designer's own reference photos. The browser
// preview is a separate implementation and drifts from production; this is the
// only output that is true by construction. Renders land in ~2 minutes, so the
// caller starts a run and then polls fetchProofStatus.
//
// textOverrides sets the {{token}} values for this run ({first_name, grade,
// school_name, ...}) so a look can be checked against the longest and shortest
// real text. Omit a token to render the fixture student's real value.
export const startProofRender = (season, lookTemplateId, textOverrides) =>
  api.post('/admin/favorites_designer/render_proof', {
    season,
    look_template_id: lookTemplateId,
    text_overrides: textOverrides || {},
  }).then(r => r.data);

// Starting values for the {{token}} boxes, plus the fixed grade list (grade is
// not free text — the renderer maps a known set and blanks anything else).
export const fetchProofTextDefaults = (season) =>
  api.get('/admin/favorites_designer/proof_text_defaults', { params: { season } }).then(r => r.data);

export const fetchProofStatus = (runId) =>
  api.get('/admin/favorites_designer/proof_status', { params: { run_id: runId } }).then(r => r.data);
